import { get, isEmpty } from 'lodash';
import { INPUT_AUTOCOMPLETE, INPUT_TYPE, ORDER_STATUS, PAYMENT_TYPES } from '@nandosaus/constants';
import { observer } from 'mobx-react';
import { useCallback, useEffect, useState } from 'react';
import { useFormikContext } from 'formik';
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';
import { useRootStore } from '@nandosaus/state-management';
import { useRouter } from 'next/router';
import dynamic from 'next/dynamic';
import PropTypes from 'prop-types';
import styled from 'styled-components';

import { amountIsValid, autoFormatAmount, errorMessage as formErrorMessage, getAmountValue } from './util';
import { Box, Flex } from '../grid';
import { Button } from '../button';
import { Form } from '../form';
import { FormikTextareaInput, FormikTextInput, TextInput } from '../input';
import { Link } from '../link';
import { LoadingIndicator } from '../icons/loading-indicator';
import { navigation } from '../../constants';
import { P, Subheading } from '../typography';
import { Payment } from '../payment';
import { useApplePay } from '../../hooks/useApplePay';
import { useGooglePay } from '../../hooks/useGooglePay';
import { useSchema } from '../../hooks/use-schema';
import { persistGiftCardCart, restoreGiftCardCart } from '../../utils/data';
import { logger } from '../../utils/logger';

const OrderCheckoutModals = dynamic(() => import('../order-checkout-modals').then(mod => mod.OrderCheckoutModals), {
  ssr: false,
});

const StyledButton = styled(Button)`
  ${({ isSelected, theme }) =>
    !isSelected &&
    `
    border: 1px solid ${theme.colors.grey300};
    color: ${theme.colors.grey500};
  `};
`;

const toOrderInput = ({ formValues, amount, quantity, token }) => ({
  ...formValues,
  amount: getAmountValue(amount),
  quantity,
  token,
});

const WrappedOrderCheckoutModals = ({ handleSuccess, paymentChallengeAction, amount, quantity }) => {
  const { CartStore, MemberStore } = useRootStore();
  const { values } = useFormikContext();
  const { executeRecaptcha } = useGoogleReCaptcha();

  return (
    <OrderCheckoutModals
      onChallengeSuccess={handleSuccess}
      paymentChallengeAction={paymentChallengeAction}
      createOrder={async adyenNativeResponse => {
        let token;
        if (!MemberStore.profile) {
          token = await executeRecaptcha('create_gift_card');
        }
        const orderInput = toOrderInput({ formValues: values, amount, quantity, token });
        return CartStore.createGiftCardOrder(orderInput, adyenNativeResponse);
      }}
    />
  );
};

WrappedOrderCheckoutModals.propTypes = {
  amount: PropTypes.string,
  quantity: PropTypes.string.isRequired,
  handleSuccess: PropTypes.func.isRequired,
  paymentChallengeAction: PropTypes.shape(),
};

WrappedOrderCheckoutModals.defaultProps = {
  amount: null,
  paymentChallengeAction: undefined,
};

const CreateGiftCardForm = ({ amount, setAmount, quantity, adyen, isProcessing3dsPayment }) => {
  const [errorMessage, setErrorMessage] = useState(null);
  const [paymentChallengeAction, setPaymentChallengeAction] = useState();
  const validationSchema = useSchema('createGiftCard');
  const router = useRouter();
  const { executeRecaptcha } = useGoogleReCaptcha();
  const rootStore = useRootStore();
  const { CartStore, MemberStore, OrderStore } = rootStore;
  const { allowCreateGiftCardOrder, createGiftCardOrder } = CartStore;

  const { isValidating, status: orderStatus } = OrderStore;

  const createOrderWithGooglePay = useGooglePay({ submitOrder: createGiftCardOrder });
  const createOrderWithApplePay = useApplePay({ submitOrder: createGiftCardOrder });

  const handleSuccess = useCallback(() => {
    router.push({ pathname: navigation.CREATE_GIFT_CARD_SUCCESS });
  }, [router]);

  const onSubmit = useCallback(
    async values => {
      let token;
      if (!MemberStore.profile) {
        token = await executeRecaptcha('create_gift_card');
      }

      try {
        const orderInput = toOrderInput({ formValues: values, amount, quantity, token });

        let results;
        const orderSummary = { price: (orderInput.amount * orderInput.quantity).toString() };
        if (CartStore.hasPaymentType(PAYMENT_TYPES.GOOGLE_PAY)) {
          results = await createOrderWithGooglePay(orderSummary, orderInput);
        } else if (CartStore.hasPaymentType(PAYMENT_TYPES.APPLE_PAY)) {
          results = await createOrderWithApplePay(orderSummary, orderInput);
        } else {
          results = await createGiftCardOrder(orderInput, null);
        }

        const { submitted, action, errorMessage: resultsErrorMessage } = results || {};
        if (action) {
          // Note: Persist the cart for 3DS1 flows here in case they are guest orders
          persistGiftCardCart(rootStore, orderInput);

          setPaymentChallengeAction(action);
          return;
        }
        if (submitted) {
          handleSuccess();
        } else if (resultsErrorMessage) {
          setErrorMessage(resultsErrorMessage);
        }
      } catch (error) {
        const message = get(error, 'errors[0].message', error.message);
        setErrorMessage(message);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      amount,
      quantity,
      executeRecaptcha,
      CartStore.paymentType,
      MemberStore.profile,
      createGiftCardOrder,
      createOrderWithApplePay,
      createOrderWithGooglePay,
      handleSuccess,
      rootStore,
    ]
  );

  /**
   * Resubmit order with additional payment details after returning from
   * a 3DS challenge redirection.
   */
  useEffect(() => {
    if (isProcessing3dsPayment) {
      const orderInput = restoreGiftCardCart(rootStore);

      if (!orderInput) {
        logger.error('Could not restore gift card cart after 3DS challenge.');
        return;
      }

      const { MD, PaRes } = adyen;
      const paymentDetails = { MD, PaRes };

      CartStore.createGiftCardOrder(orderInput, { paymentDetails }).then(({ submitted }) => {
        if (submitted) {
          handleSuccess();
        }
      });
    }
  }, [isProcessing3dsPayment, CartStore, adyen, handleSuccess, rootStore]);

  if (MemberStore.loading)
    return (
      <Flex justifyContent="flex" mt={4}>
        <LoadingIndicator />
      </Flex>
    );

  const { givenName, familyName, email } = MemberStore.profile || {};

  // eslint-disable-next-line react/no-unstable-nested-components
  const PriceButton = ({ buttonAmount, ...rest }) => {
    const isSelected = amount === buttonAmount;
    return (
      <StyledButton
        isSelected={isSelected}
        variant={isSelected ? 'primary' : 'secondary'}
        width="100%"
        onClick={() => setAmount(buttonAmount)}
        {...rest}
      >
        ${buttonAmount}
      </StyledButton>
    );
  };
  PriceButton.propTypes = { buttonAmount: PropTypes.string.isRequired };

  return (
    <Form
      error={errorMessage}
      disabled={
        amount < 10 ||
        !allowCreateGiftCardOrder ||
        isValidating ||
        orderStatus !== ORDER_STATUS.NEW ||
        !amountIsValid(amount)
      }
      heading={<Subheading textAlign="center">Create an e-gift card</Subheading>}
      initialValues={{
        sendersGivenName: givenName || '',
        sendersSurname: familyName || '',
        sendersEmail: email || '',
        recipientsGivenName: '',
        recipientsSurname: '',
        recipientsEmail: '',
        message: '',
      }}
      onSubmit={onSubmit}
      validationSchema={validationSchema}
      buttonText="Order Nando’s e-gift card"
      paragraph={
        <P textAlign="center" variant={3} mt={2} mb={2}>
          Email a gift card with a personal message. Valid for 3yrs and redeemable online or in-store.
        </P>
      }
    >
      <WrappedOrderCheckoutModals {...{ handleSuccess, paymentChallengeAction, amount, quantity }} />
      <P variant={2} fontWeight="bold" mb={1}>
        1. Details
      </P>

      <Flex mb={1}>
        <Box mr={1} width="100%">
          <FormikTextInput name="sendersGivenName" label="Sender’s Given Name" type={INPUT_TYPE.TEXT} />
        </Box>
        <Box width="100%">
          <FormikTextInput name="sendersSurname" label="Sender’s Surname" type={INPUT_TYPE.TEXT} />
        </Box>
      </Flex>

      <Box mb={1}>
        <FormikTextInput
          name="sendersEmail"
          label="Sender’s Email"
          type={INPUT_TYPE.EMAIL}
          autoComplete={INPUT_AUTOCOMPLETE.EMAIL}
        />
      </Box>

      <Flex mb={1}>
        <Box mr={1} width="100%">
          <FormikTextInput name="recipientsGivenName" label="Recipient’s Given Name" type={INPUT_TYPE.TEXT} />{' '}
        </Box>
        <Box width="100%">
          <FormikTextInput name="recipientsSurname" label="Recipient’s Surname" type={INPUT_TYPE.TEXT} />{' '}
        </Box>
      </Flex>

      <Box mb={1}>
        <FormikTextInput
          name="recipientsEmail"
          label="Recipient’s Email"
          type={INPUT_TYPE.EMAIL}
          autoComplete={INPUT_AUTOCOMPLETE.EMAIL}
        />
      </Box>

      <Box mb={1}>
        <FormikTextareaInput label="Message" name="message" rows={4} maxLength={250} />
        <P color="greyPrimary" variant={3}>
          Optional (up to 250 characters)
        </P>
      </Box>
      <P variant={2} fontWeight="bold" mb={1} mt={2}>
        2. Amount
      </P>
      <Flex width="100%" justifyContent="space-between" mb={1}>
        <PriceButton buttonAmount="50" />
        <PriceButton buttonAmount="100" mx={1} />
        <PriceButton buttonAmount="200" />
      </Flex>
      <Box mb={1}>
        <TextInput
          name="customAmount"
          label="Custom Amount"
          placeholder="$"
          type={INPUT_TYPE.TEXT}
          value={autoFormatAmount(amount)}
          onChange={event => setAmount(event.target.value)}
          isError={!isEmpty(amount) && (!amountIsValid(amount) || getAmountValue(amount) < 10)}
          errorMessage={formErrorMessage(amount)}
        />
      </Box>
      <P variant={2} fontWeight="bold" mb={1} mt={2}>
        3. Payment
      </P>
      <Payment hiddenPaymentTypes={[PAYMENT_TYPES.GIFT_CARD, PAYMENT_TYPES.LOYALTY, PAYMENT_TYPES.PAYPAL]} hideTitle />
      <P variant={4} mt={1} mb={2}>
        By ordering you agree to the{' '}
        <Link variant={4} target="_blank" href="/t-c-gift-card">
          Gift Card Terms and Conditions
        </Link>
        .
      </P>
    </Form>
  );
};

CreateGiftCardForm.propTypes = {
  amount: PropTypes.string,
  setAmount: PropTypes.func.isRequired,
  quantity: PropTypes.string.isRequired,
  isProcessing3dsPayment: PropTypes.bool,
  adyen: PropTypes.shape({
    MD: PropTypes.string,
    PaRes: PropTypes.string,
  }),
};

CreateGiftCardForm.defaultProps = {
  amount: null,
  isProcessing3dsPayment: false,
  adyen: {
    MD: null,
    PaRes: null,
  },
};

const ObservedCreateGiftCardForm = observer(CreateGiftCardForm);
export { ObservedCreateGiftCardForm as CreateGiftCardForm };
